import { Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getTaxInfo } from '@/api/tax-info';

import {
  InfoReviewFieldLabel,
  InfoReviewFieldValue,
} from '@/components/Form/Form.styled';
import { Divider } from '@/components/Layout/Divider';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/baseSelectors';

import {
  IndiciaReasonCode,
  NoTaxNumberReasonCode,
  NoTaxResidencyReasonCode,
  TaxInfoEntry,
  TaxNumberTypeCode,
  UsTaxNoReturnReasonCode,
} from '@/types/api/tax-info';

import { getForeignNonTaxResidencyEntries } from '../../drawers/TaxInfoDrawer/ForeignNonTaxResidencySection/utils';
import {
  getNoTaxNumberReasonCategoryForCode,
  isNoTaxNumberReasonCategory,
  isNoTaxNumberReasonCode,
} from '../../drawers/TaxInfoDrawer/ForeignNoTaxNumberReasonSection/utils';
import { getForeignTaxResidencyEntries } from '../../drawers/TaxInfoDrawer/ForeignTaxResidencySection/utils';
import { getUsTaxEntry } from '../../drawers/TaxInfoDrawer/QuestionUSTaxReturn/utils';
import {
  deriveTaxQuestionAnswers,
  getForeignIndiciaExplanationEntries,
} from '../../drawers/TaxInfoDrawer/utils/taxInfoStatus';
import { ReviewCard } from './ReviewCard';
import { SectionProps } from './types';

const FIELD_PREFIX = 'moreInfo.reviewMyInformation.tax.field';

export function TaxSection({
  currentIndex,
  totalSections,
}: Readonly<SectionProps>) {
  const { t, country } = useTranslate();
  const { isoCode: tenantCountryCode } = useAppSelector(selectConfigsState);

  const { data: taxInfo } = useQuery({
    queryKey: ['taxInfo'],
    queryFn: getTaxInfo,
  });

  const questionCountryParams = useMemo(
    () => ({
      tenantCountry: country[tenantCountryCode] ?? '',
    }),
    [tenantCountryCode, country],
  );

  const answers = deriveTaxQuestionAnswers(taxInfo);
  const usTaxEntry = getUsTaxEntry(taxInfo);
  const foreignTaxResidencies = getForeignTaxResidencyEntries(taxInfo);
  const foreignNonTaxResidencies = getForeignNonTaxResidencyEntries(taxInfo);
  const indiciaExplanations = getForeignIndiciaExplanationEntries(taxInfo);

  const formatYesNoAnswer = (value?: boolean) => {
    return value ? t('common.yes') : t('common.no');
  };

  const shouldShowDivider =
    usTaxEntry?.usTaxReturnRequired ||
    usTaxEntry?.usTaxNoReturnReasonCode ||
    foreignTaxResidencies.length > 0 ||
    foreignNonTaxResidencies.length > 0 ||
    indiciaExplanations.length > 0;

  return (
    <ReviewCard
      title={t('moreInfo.reviewMyInformation.tax.title')}
      currentIndex={currentIndex}
      totalSections={totalSections}
    >
      <InfoReviewFieldLabel>
        {t('taxInfoDrawer.question1.question')}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {formatYesNoAnswer(answers.usTaxReturnRequired)}
      </InfoReviewFieldValue>

      <InfoReviewFieldLabel>
        {t('taxInfoDrawer.question2.foreignIndicia.question', {
          ...questionCountryParams,
        })}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {formatYesNoAnswer(answers.hasForeignConnection)}
      </InfoReviewFieldValue>

      <InfoReviewFieldLabel>
        {t('taxInfoDrawer.question2.foreignTaxResident.question', {
          ...questionCountryParams,
        })}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {formatYesNoAnswer(answers.isForeignTaxResident)}
      </InfoReviewFieldValue>

      {shouldShowDivider && <Divider width="100%" sx={{ mt: 1.5 }} />}

      {usTaxEntry && <USTaxResidency entry={usTaxEntry} />}

      {foreignTaxResidencies.map(entry => (
        <ForeignTaxResidency key={entry.countryCode} entry={entry} />
      ))}

      {foreignNonTaxResidencies.map(entry => (
        <ForeignNonTaxResidency key={entry.countryCode} entry={entry} />
      ))}

      {indiciaExplanations.map(entry => (
        <IndiciaExplanation key={entry.countryCode} entry={entry} />
      ))}
    </ReviewCard>
  );
}

// --- Sub-components based on TaxInfo type ---

type TaxInfoEntryProps = {
  entry: TaxInfoEntry;
};

function USTaxResidency({ entry }: Readonly<TaxInfoEntryProps>) {
  const { t } = useTranslate();

  if (entry.usTaxReturnRequired) {
    //render US tax residency with tax number and type
    return (
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.taxResidence`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {t(`${FIELD_PREFIX}.usCountryName`)}
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            <TaxNumberLabel
              entry={entry}
              countryName={t(`${FIELD_PREFIX}.usCountryName`)}
            />
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>{entry.taxNumberValue}</InfoReviewFieldValue>
        </Grid>
      </Grid>
    );
  }

  if (!entry.usTaxNoReturnReasonCode) {
    return null;
  }

  // render US tax non-residency reason
  const reasonText =
    entry.usTaxNoReturnReasonCode === UsTaxNoReturnReasonCode.OTHER
      ? (entry.usTaxNoReturnReasonOther ?? '')
      : t(
          `taxInfoDrawer.question1.noReturnReason.options.${entry.usTaxNoReturnReasonCode}`,
        );

  return (
    <>
      <InfoReviewFieldLabel>
        {t(`${FIELD_PREFIX}.noUsTaxReturnReason`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>{reasonText}</InfoReviewFieldValue>
    </>
  );
}

function ForeignTaxResidency({ entry }: Readonly<TaxInfoEntryProps>) {
  const { t, country } = useTranslate();

  const countryName = country[entry.countryCode] || entry.countryCode || '';

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.taxResidence`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>{countryName}</InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            <TaxNumberLabel entry={entry} countryName={countryName} />
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {entry.taxNumberValue || t(`${FIELD_PREFIX}.none`)}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>
      {!entry.taxNumberValue && (
        <>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.noTaxNumberFor`, { country: countryName })}
          </InfoReviewFieldLabel>
          <NoTaxNumberReasonValue entry={entry} />
        </>
      )}
    </>
  );
}

function ForeignNonTaxResidency({ entry }: Readonly<TaxInfoEntryProps>) {
  const { t, country } = useTranslate();

  const countryName = country[entry.countryCode] || entry.countryCode || '';

  return (
    <>
      <InfoReviewFieldLabel>
        {t('taxInfoDrawer.question2.foreignNonTaxResidency.blockTitle')}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>{countryName}</InfoReviewFieldValue>
      <InfoReviewFieldLabel>
        {t(`${FIELD_PREFIX}.whyNoTaxes`, { country: countryName })}
      </InfoReviewFieldLabel>
      <NoTaxResidencyReasonValue entry={entry} />
    </>
  );
}

function IndiciaExplanation({ entry }: Readonly<TaxInfoEntryProps>) {
  const { t, country } = useTranslate();

  return (
    <>
      <InfoReviewFieldLabel>
        {t('taxInfoDrawer.question2.foreignIndicia.countryQuestion', {
          country: country[entry.countryCode] || entry.countryCode || '',
        })}
      </InfoReviewFieldLabel>
      <IndiciaExplanationValue entry={entry} />
    </>
  );
}

// --- Display label/value components ---

function TaxNumberLabel({
  entry,
  countryName,
}: Readonly<{ entry: TaxInfoEntry; countryName: string }>) {
  const { t } = useTranslate();

  if (!entry.taxNumberTypeCode) {
    return (
      <>
        {t(`${FIELD_PREFIX}.countryTaxNumber`, {
          country: countryName,
        })}
      </>
    );
  }

  if (entry.taxNumberTypeCode === TaxNumberTypeCode.OTHER) {
    return <>{`${countryName} ${entry.otherTaxNumberType ?? ''}`.trim()}</>;
  }

  return (
    <>
      {countryName} {entry.taxNumberTypeCode}
    </>
  );
}

function NoTaxNumberReasonValue({ entry }: Readonly<{ entry: TaxInfoEntry }>) {
  const { t } = useTranslate();

  const reason = useMemo(() => {
    if (entry.noTaxNumberReasonOther) {
      return entry.noTaxNumberReasonOther;
    }

    const code = entry.noTaxNumberReasonCode;

    if (
      isNoTaxNumberReasonCode(code) &&
      code !== NoTaxNumberReasonCode.COUNTRY_DOES_NOT_ISSUE_TIN
    ) {
      return t(
        `taxInfoDrawer.question2.foreignTaxResidency.noTaxNumberReason.codeOptions.${code}`,
      );
    }

    const category = isNoTaxNumberReasonCategory(
      entry.noTaxNumberReasonCategory,
    )
      ? entry.noTaxNumberReasonCategory
      : getNoTaxNumberReasonCategoryForCode(code);

    return category
      ? t(
          `taxInfoDrawer.question2.foreignTaxResidency.noTaxNumberReason.options.${category}`,
        )
      : '';
  }, [
    entry.noTaxNumberReasonOther,
    entry.noTaxNumberReasonCode,
    entry.noTaxNumberReasonCategory,
    t,
  ]);

  return (
    <InfoReviewFieldValue>
      {t(`${FIELD_PREFIX}.reason`, { reason })}
    </InfoReviewFieldValue>
  );
}

function NoTaxResidencyReasonValue({
  entry,
}: Readonly<{ entry: TaxInfoEntry }>) {
  const { t } = useTranslate();

  if (entry.noTaxResidencyReasonCode === NoTaxResidencyReasonCode.OTHER) {
    return (
      <InfoReviewFieldValue>
        {entry.noTaxResidencyReasonOther ?? ''}
      </InfoReviewFieldValue>
    );
  }

  if (!entry.noTaxResidencyReasonCode) {
    return <InfoReviewFieldValue>{''}</InfoReviewFieldValue>;
  }

  return (
    <InfoReviewFieldValue>
      {t(
        `taxInfoDrawer.question2.foreignNonTaxResidency.reason.options.${entry.noTaxResidencyReasonCode}`,
      )}
    </InfoReviewFieldValue>
  );
}

function IndiciaExplanationValue({ entry }: Readonly<{ entry: TaxInfoEntry }>) {
  const { t } = useTranslate();

  if (entry.indiciaReasonCode === IndiciaReasonCode.OTHER) {
    return (
      <InfoReviewFieldValue>
        {entry.indiciaReasonOther ?? ''}
      </InfoReviewFieldValue>
    );
  }

  if (!entry.indiciaReasonCode) {
    return <InfoReviewFieldValue>{''}</InfoReviewFieldValue>;
  }

  return (
    <InfoReviewFieldValue>
      {t(
        `taxInfoDrawer.question2.foreignIndicia.options.${entry.indiciaReasonCode}`,
      )}
    </InfoReviewFieldValue>
  );
}
